import { ReactNode } from 'react';
import { createUseStyles } from 'react-jss';
import { colors } from '@packages/core/styles';
import { Form } from 'react-bootstrap';
import classNames from 'classnames';

const useStyles = createUseStyles({
    card: {
        gridColumn: '1 / -1',
        display: 'grid',
        gridTemplateColumns: 'subgrid',
        alignItems: 'center',
        padding: '8px 16px',
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 4,
        '&:hover': {
            backgroundColor: colors.grayOne,
        },
    },
    cardSelected: {
        borderColor: colors.blueOne,
        backgroundColor: colors.blueFour,
        '&:hover': {
            backgroundColor: colors.blueFour,
        },
    },
    cardDisabled: {
        opacity: 0.6,
        pointerEvents: 'none',
    },
    control: {
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        '& label': {
            display: 'flex',
            flexGrow: 1,
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '8px',
            minHeight: 24,
            marginBottom: 0,
            minWidth: 0,
            cursor: 'pointer',
        },
    },
    rightSideContent: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: `16px`,
    },
});

export type RadioCardProps = {
    children: ReactNode;
    isCardDisabled?: boolean;
    isSelected: boolean;
    name: string;
    onSelect: (value: string) => void;
    rightSideContent?: ReactNode;
    value: string;
};

/**
 * A single card representing an option within a `RadioCardGroup`.
 *
 * Renders a radio input with a label and optional right side content, styled as a card.
 *
 * @param props - The props for the `RadioCard` component.
 * @returns The rendered `RadioCard` component.
 */
export function RadioCard({
    children,
    isCardDisabled = false,
    isSelected,
    name,
    onSelect,
    rightSideContent,
    value,
}: RadioCardProps) {
    const classes = useStyles();

    const hasRightSideContent = rightSideContent !== undefined && rightSideContent !== null;

    function handleSelect() {
        onSelect(value);
    }

    return (
        <div
            className={classNames(
                classes.card,
                isSelected && classes.cardSelected,
                isCardDisabled && classes.cardDisabled
            )}
        >
            <Form.Check
                checked={isSelected}
                className={classes.control}
                disabled={isCardDisabled}
                id={`${name}-${value}`}
                label={children}
                name={name}
                onChange={handleSelect}
                type="radio"
                value={value}
            />

            {hasRightSideContent && <div className={classes.rightSideContent}>{rightSideContent}</div>}
        </div>
    );
}
